import * as XLSX from "xlsx";

export const REQUIRED_SHEETS = [
  "General",
  "Product Information",
  "MMS CCS Criteria",
  "Chemical Assessment",
  "Audit Summary",
  "NC Summary",
  "Certification Decision",
] as const;

export interface ExtractedAuditData {
  selectedCbName: string;
  cbCode: string;
  cbCodeSuffix: string;
  auditStartDate: string; // YYYY-MM-DD
  auditEndDate: string;   // YYYY-MM-DD
  teId: string;           // Original TE-ID string e.g. TE-56895656
  teIdDigits: string;     // Digits portion e.g. 56895656
  startDateYYYYMMDD: string; // e.g. 20260112
  endDateYYYYMMDD: string;   // e.g. 20260113
  startDateSerial: string;   // e.g. 46034
  endDateSerial: string;     // e.g. 46035
  parsedSheets: string[];
}

export interface ValidationStepResult {
  step: 1 | 2 | 3 | 4;
  title: string;
  passed: boolean;
  message?: string;
  details?: string[];
}

/**
 * Converts Excel serial date or Date or string into ISO YYYY-MM-DD string,
 * YYYYMMDD string, and Excel serial date number string.
 */
function parseExcelDate(val: any): { isoDate: string; yyyymmdd: string; serialStr: string } {
  if (val === undefined || val === null || val === "") {
    throw new Error("Date cell is empty or missing.");
  }

  let jsDate: Date | null = null;
  let serialNum: number | null = null;

  if (typeof val === "number") {
    serialNum = Math.floor(val);
    // Excel epoch offset: Jan 1 1900 is 1, but Excel treats 1900 as leap year (bug)
    // 25569 is 1970-01-01 in Excel serial days
    const utcDays = serialNum - 25569;
    const utcValue = utcDays * 86400 * 1000;
    jsDate = new Date(utcValue);
  } else if (val instanceof Date) {
    jsDate = val;
    const epoch = Date.UTC(1899, 11, 30);
    serialNum = Math.floor((val.getTime() - epoch) / (24 * 60 * 60 * 1000));
  } else if (typeof val === "string") {
    const trimmed = val.trim();
    // Check if numeric string
    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      serialNum = Math.floor(parseFloat(trimmed));
      const utcDays = serialNum - 25569;
      jsDate = new Date(utcDays * 86400 * 1000);
    } else {
      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) {
        jsDate = parsed;
        const epoch = Date.UTC(1899, 11, 30);
        serialNum = Math.floor((parsed.getTime() - epoch) / (24 * 60 * 60 * 1000));
      } else {
        throw new Error(`Invalid date format string: '${val}'`);
      }
    }
  }

  if (!jsDate || isNaN(jsDate.getTime())) {
    throw new Error(`Unable to parse date value: ${val}`);
  }

  const year = jsDate.getUTCFullYear();
  const month = String(jsDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(jsDate.getUTCDate()).padStart(2, "0");

  const isoDate = `${year}-${month}-${day}`;
  const yyyymmdd = `${year}${month}${day}`;
  const serialStr = serialNum ? String(serialNum) : "";

  return { isoDate, yyyymmdd, serialStr };
}

function isValidYYYYMMDD(str: string): boolean {
  if (!/^\d{8}$/.test(str)) return false;
  const year = parseInt(str.slice(0, 4), 10);
  const month = parseInt(str.slice(4, 6), 10);
  const day = parseInt(str.slice(6, 8), 10);
  if (month < 1 || month > 12) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/**
 * Helper to find sheet name in workbook case-insensitively.
 */
export function findSheetNameCaseInsensitive(sheetNames: string[], targetName: string): string | undefined {
  return sheetNames.find(
    (name) => name.trim().toLowerCase() === targetName.trim().toLowerCase()
  );
}

/**
 * Extracts CB Data, TE ID, and Audit Dates from uploaded Excel ArrayBuffer.
 */
export function extractCbDataFromExcel(buffer: ArrayBuffer | Uint8Array): ExtractedAuditData {
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true });

  const availableSheets = workbook.SheetNames;

  // Validate all required sheets case-insensitively first
  const missingSheets: string[] = REQUIRED_SHEETS.filter(
    (reqSheet) => !findSheetNameCaseInsensitive(availableSheets, reqSheet)
  );
  const refSheetName = findSheetNameCaseInsensitive(availableSheets, "Ref");

  if (missingSheets.length > 0 || !refSheetName) {
    const allMissing: string[] = [...missingSheets];
    if (!refSheetName && !allMissing.includes("Ref")) {
      allMissing.push("Ref");
    }
    throw new Error(`Missing required worksheet(s): ${allMissing.join(", ")}`);
  }

  const generalSheetName = findSheetNameCaseInsensitive(availableSheets, "General")!;
  const generalSheet = workbook.Sheets[generalSheetName];
  if (!generalSheet) {
    throw new Error("Worksheet 'General' could not be loaded.");
  }

  const fieldErrors: string[] = [];

  // Read Cell N15 (Selected CB Name)
  const cellN15 = generalSheet["N15"];
  const selectedCbName = cellN15 ? String(cellN15.v).trim() : "";
  if (!selectedCbName) {
    fieldErrors.push("Selected Certification Body Name in cell N15 of 'General' sheet is empty or missing.");
  }

  // Read Cell N33 (Certified Organization TE-ID)
  const cellN33 = generalSheet["N33"];
  const teIdRaw = cellN33 ? String(cellN33.v).trim() : "";
  if (!teIdRaw) {
    fieldErrors.push("Certified Organization TE-ID in cell N33 of 'General' sheet is empty or missing.");
  }
  const teIdDigits = teIdRaw.replace(/^[^\d]+/, "").replace(/\D/g, "") || teIdRaw;

  // Read Cell N99 (Audit Start Date)
  const cellN99 = generalSheet["N99"];
  let startDateParsed = { isoDate: "", yyyymmdd: "", serialStr: "" };
  if (!cellN99 || cellN99.v === undefined || cellN99.v === null || cellN99.v === "") {
    fieldErrors.push("Audit Start Date in cell N99 of 'General' sheet is empty or missing.");
  } else {
    try {
      startDateParsed = parseExcelDate(cellN99.v);
    } catch (e: any) {
      fieldErrors.push(`Audit Start Date in cell N99 of 'General' sheet: ${e.message}`);
    }
  }

  // Read Cell N101 (Audit End Date)
  const cellN101 = generalSheet["N101"];
  let endDateParsed = { isoDate: "", yyyymmdd: "", serialStr: "" };
  if (!cellN101 || cellN101.v === undefined || cellN101.v === null || cellN101.v === "") {
    fieldErrors.push("Audit End Date in cell N101 of 'General' sheet is empty or missing.");
  } else {
    try {
      endDateParsed = parseExcelDate(cellN101.v);
    } catch (e: any) {
      fieldErrors.push(`Audit End Date in cell N101 of 'General' sheet: ${e.message}`);
    }
  }

  // Parse Ref sheet to map CB Name to CB Code
  const refSheet = workbook.Sheets[refSheetName];
  let cbCode = "";
  if (selectedCbName) {
    if (!refSheet) {
      fieldErrors.push("Worksheet 'Ref' could not be loaded.");
    } else {
      const refRange = XLSX.utils.decode_range(refSheet["!ref"] || "A1:KW500");
      const colKV = XLSX.utils.decode_col("KV"); // Column index 308 (0-indexed 307)
      const colKW = XLSX.utils.decode_col("KW"); // Column index 309 (0-indexed 308)

      // Scan starting at row 2 (0-indexed row 1)
      for (let r = 1; r <= refRange.e.r; r++) {
        const cellNameAddr = XLSX.utils.encode_cell({ r, c: colKV });
        const cellCodeAddr = XLSX.utils.encode_cell({ r, c: colKW });

        const nameCell = refSheet[cellNameAddr];
        const codeCell = refSheet[cellCodeAddr];

        if (nameCell && nameCell.v) {
          const nameVal = String(nameCell.v).trim();
          if (nameVal.toLowerCase() === selectedCbName.toLowerCase()) {
            cbCode = codeCell && codeCell.v ? String(codeCell.v).trim() : "";
            break;
          }
        }
      }

      if (!cbCode) {
        fieldErrors.push(`Certification Body Name '${selectedCbName}' (from cell N15) was not found in 'Ref' sheet column KV.`);
      }
    }
  }

  if (fieldErrors.length > 0) {
    throw new Error(`FIELD_ERRORS::${JSON.stringify(fieldErrors)}`);
  }

  const cbCodeSuffix = cbCode.includes("-") ? cbCode.split("-").pop() || cbCode : cbCode;

  return {
    selectedCbName,
    cbCode,
    cbCodeSuffix,
    auditStartDate: startDateParsed.isoDate,
    auditEndDate: endDateParsed.isoDate,
    teId: teIdRaw,
    teIdDigits,
    startDateYYYYMMDD: startDateParsed.yyyymmdd,
    endDateYYYYMMDD: endDateParsed.yyyymmdd,
    startDateSerial: startDateParsed.serialStr,
    endDateSerial: endDateParsed.serialStr,
    parsedSheets: availableSheets,
  };
}

export function validateAuditFileName(
  fileName: string,
  data: ExtractedAuditData
): { valid: boolean; error?: string; expectedPatterns?: string[] } {
  // Strip file extension (.xlsx or .xls)
  const baseName = fileName.replace(/\.(xlsx|xls)$/i, "");
  
  if (!fileName.match(/\.(xlsx|xls)$/i)) {
    return { valid: false, error: "File extension must be .xlsx or .xls" };
  }

  const expectedDatePattern1 = `${data.startDateYYYYMMDD}${data.endDateYYYYMMDD}`;
  const expectedDatePattern2 = data.startDateSerial && data.endDateSerial
    ? `${data.startDateSerial}${data.endDateSerial}`
    : "";

  const cbCodePrimary = data.cbCodeSuffix || data.cbCode;

  const today = new Date();
  const sampleSubDate = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;

  const samplePattern1 = `AD_${cbCodePrimary}_${data.teIdDigits}_${expectedDatePattern1}_${sampleSubDate}`;
  const samplePattern2 = expectedDatePattern2
    ? `AD_${cbCodePrimary}_${data.teIdDigits}_${expectedDatePattern2}_${sampleSubDate}`
    : null;

  const parts = baseName.split("_");
  let isMatch = false;
  let subDateError = false;

  if (parts.length === 5 && parts[0].toUpperCase() === "AD") {
    const matchCb = [data.cbCode.toLowerCase(), data.cbCodeSuffix.toLowerCase()].includes(parts[1].toLowerCase());
    const matchTe = [data.teId.toLowerCase(), data.teIdDigits.toLowerCase()].includes(parts[2].toLowerCase());
    const matchDates = [expectedDatePattern1.toLowerCase(), expectedDatePattern2.toLowerCase()].filter(Boolean).includes(parts[3].toLowerCase());
    const validSubDate = isValidYYYYMMDD(parts[4]);

    if (matchCb && matchTe && matchDates) {
      if (validSubDate) {
        isMatch = true;
      } else {
        subDateError = true;
      }
    }
  }

  if (!isMatch) {
    const patternsList = [samplePattern1];
    if (samplePattern2) patternsList.push(samplePattern2);
    const errorMsg = subDateError
      ? `File name '${fileName}' has an invalid submission date section '${parts[4]}'. Expected a valid date in YYYYMMDD format (e.g. ${sampleSubDate}).`
      : `File name '${fileName}' does not match expected pattern. Expected format: AD_<CB_Code>_<TE_ID>_<Audit Dates>_<Submission Date>.xlsx (e.g. ${samplePattern1}.xlsx)`;

    return {
      valid: false,
      error: errorMsg,
      expectedPatterns: patternsList,
    };
  }

  return { valid: true };
}
